import React, { useId } from 'react';
import { ClinicalSituation, ConsciousLevel, OxygenScale } from '../types';

interface SituationScenarioCardProps {
  situation: ClinicalSituation;
  onGoToCalculator: () => void;
  onChange: () => void;
  onClear: () => void;
}

const formatTemperature = (value: number) => value.toFixed(1).replace('.', ',');

const SituationScenarioCard = React.forwardRef<
  HTMLElement,
  SituationScenarioCardProps
>(({ situation, onGoToCalculator, onChange, onClear }, ref) => {
  const titleId = useId();
  const vitalsTitleId = useId();
  const { vitals } = situation;
  const vitalItems = [
    { label: 'Kvėpavimo dažnis', value: `${vitals.respirationRate} k./min` },
    { label: 'Deguonies saturacija', value: `${vitals.oxygenSaturation} %` },
    { label: 'Deguonies terapija', value: vitals.airOrOxygen === 'Oxygen' ? 'Deguonis' : 'Oras' },
    {
      label: 'SpO₂ skalė',
      value: vitals.oxygenScale === OxygenScale.SCALE_2 ? '2 skalė' : '1 skalė',
    },
    { label: 'Sistolinis AKS', value: `${vitals.systolicBP} mmHg` },
    { label: 'Pulsas', value: `${vitals.pulseRate} k./min` },
    {
      label: 'Sąmonė',
      value: vitals.consciousness === ConsciousLevel.ALERT ? 'Budrus (A)' : 'CVPU',
    },
    { label: 'Temperatūra', value: `${formatTemperature(vitals.temperature)} °C` },
  ];

  return (
    <section
      ref={ref}
      className="situation-scenario-shell situation-scenario-card"
      tabIndex={-1}
      aria-labelledby={titleId}
    >
      <div className="situation-scenario-card__header">
        <div>
          <span className="situation-scenario-card__eyebrow">Mokomasis klinikinis atvejis</span>
          <h2 id={titleId}>{situation.title}</h2>
          <p>{situation.summary}</p>
        </div>
        <button className="situation-scenario-card__change" type="button" onClick={onChange}>
          Keisti situaciją
        </button>
      </div>

      <p className="situation-scenario-card__description">{situation.caseDescription}</p>

      <div className="situation-scenario-card__instruction">
        <strong>Užduotis</strong>
        <p>
          Perrašykite pateiktus gyvybinius rodiklius į žemiau esančią skaičiuoklę ir patys
          apskaičiuokite NEWS2. Situacijos pasirinkimas laukelių reikšmių nekeičia.
        </p>
      </div>

      <div className="situation-scenario-card__values" aria-labelledby={vitalsTitleId}>
        <h3 id={vitalsTitleId}>Pateikti gyvybiniai rodikliai</h3>
        <dl className="situation-vitals-grid">
          {vitalItems.map((item) => (
            <div className="situation-vital" key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="situation-scenario-card__footer">
        <div className="situation-scenario-card__actions">
          <button
            className="situation-action situation-action--primary"
            type="button"
            onClick={onGoToCalculator}
          >
            Pereiti prie rodiklių įvedimo
          </button>
          <button
            className="situation-action situation-action--secondary"
            type="button"
            onClick={onClear}
          >
            Pašalinti situaciją
          </button>
        </div>
        <a href={situation.sourceUrl} target="_blank" rel="noreferrer">
          RCP NEWS2 ataskaita (2017)
        </a>
      </div>
    </section>
  );
});

SituationScenarioCard.displayName = 'SituationScenarioCard';

export default SituationScenarioCard;
